import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { useLocation, useNavigate } from "react-router";

import { ApiError, acceptInvite, lookupInvite } from "@/api/client";
import type { InviteLookup } from "@/api/client";
import { AuthFrame } from "@/auth/AuthFrame";
import { useAuth } from "@/auth/AuthProvider";
import { Button } from "@/ui/Button";
import { Input } from "@/ui/Input";
import { Notice } from "@/ui/Notice";
import { Spinner } from "@/ui/primitives";

/** The API's `MIN_PASSWORD`. */
const MIN_PASSWORD = 12;

/**
 * Where an invite email's link lands, outside the shell. The token is in the fragment,
 * so it never reaches a server log or a referrer. Accepting signs the new member in.
 */
export function AcceptInviteScreen() {
  const { hash } = useLocation();
  const token = hash.replace(/^#/, "");
  const { adopt } = useAuth();
  const navigate = useNavigate();
  const [invite, setInvite] = useState<InviteLookup | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [again, setAgain] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setInvite(null);
    setProblem(null);
    if (!token) {
      setProblem("This link is incomplete. Open the link from the invitation email again.");
      return;
    }
    let live = true;
    lookupInvite(token)
      .then((found) => live && setInvite(found))
      .catch(
        (err: unknown) =>
          live &&
          setProblem(err instanceof ApiError ? err.detail : "The server could not be reached."),
      );
    return () => {
      live = false;
    };
  }, [token]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (password.length < MIN_PASSWORD) {
      setError(`Choose a password of at least ${MIN_PASSWORD} characters.`);
      return;
    }
    if (password !== again) {
      setError("The two passwords are not the same.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      adopt(await acceptInvite(token, name.trim(), password));
      void navigate("/sets", { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : "The server could not be reached.");
      setBusy(false);
    }
  };

  return (
    <AuthFrame>
      {problem ? (
        <Notice tone="danger">{problem}</Notice>
      ) : invite === null ? (
        <div
          className="flex justify-center py-6"
          role="status"
          aria-label="Checking the invitation"
        >
          <Spinner />
        </div>
      ) : (
        <>
          <h1 className="text-lg font-semibold text-ink">Join {invite.organization}</h1>
          <p className="mt-1 text-sm text-muted">
            You were invited as <span className="text-ink">{invite.email}</span>. Choose your name
            and a password to sign in with.
          </p>
          <form className="mt-5 space-y-4" onSubmit={(event) => void submit(event)}>
            {error ? <Notice tone="danger">{error}</Notice> : null}
            <label className="block space-y-1.5">
              <span className="text-sm font-medium text-ink">Your name</span>
              <Input
                name="name"
                autoComplete="name"
                required
                autoFocus
                value={name}
                onChange={(event) => setName(event.target.value)}
              />
            </label>
            <label className="block space-y-1.5">
              <span className="text-sm font-medium text-ink">Password</span>
              <Input
                type="password"
                name="new-password"
                autoComplete="new-password"
                required
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            </label>
            <label className="block space-y-1.5">
              <span className="text-sm font-medium text-ink">Password again</span>
              <Input
                type="password"
                name="new-password-again"
                autoComplete="new-password"
                required
                value={again}
                onChange={(event) => setAgain(event.target.value)}
              />
            </label>
            <Button type="submit" variant="primary" className="w-full" disabled={busy}>
              {busy ? <Spinner className="size-4" /> : null}
              Join
            </Button>
          </form>
        </>
      )}
    </AuthFrame>
  );
}
